import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ChatbotWidget } from "../components/ChatbotWidget";
import { customerApi } from "../lib/customerApi";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useToggle } from "../contexts/ToggleContext";
import { api, formatINR, formatApiErrorDetail } from "../lib/api";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
        if (existing) {
            existing.addEventListener("load", () => resolve(true));
            existing.addEventListener("error", () => resolve(false));
            return;
        }
        const s = document.createElement("script");
        s.src = RAZORPAY_SCRIPT;
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

export default function Checkout() {
    const navigate = useNavigate();
    const { user } = useCustomerAuth();
    const codOn = useToggle("cod");
    const coinsOn = useToggle("style_coins");
    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [codSettings, setCodSettings] = useState(null);
    const [loyalty, setLoyalty] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState("prepaid");
    const [coinsApplied, setCoinsApplied] = useState(0);
    const [address, setAddress] = useState({
        name: user?.name || "",
        phone: "",
        email: user?.email || "",
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
    });

    useEffect(() => {
        customerApi.get("/cart").then(({ data }) => setCart(data)).catch(() => setCart({ items: [] })).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (user) setAddress((a) => ({ ...a, name: a.name || user.name || "", email: a.email || user.email || "" }));
    }, [user]);

    useEffect(() => {
        if (!codOn) return;
        const pin = address.pincode?.trim();
        const url = pin ? `/cod/settings?pincode=${encodeURIComponent(pin)}` : "/cod/settings";
        api.get(url).then(({ data }) => setCodSettings(data)).catch(() => setCodSettings(null));
    }, [codOn, address.pincode]);

    useEffect(() => {
        if (!coinsOn || !user) return;
        customerApi.get("/me/loyalty").then(({ data }) => setLoyalty(data)).catch(() => setLoyalty(null));
    }, [coinsOn, user]);

    const codEligible = (() => {
        if (!codOn || !codSettings?.enabled) return false;
        const subtotal = cart?.grandTotal ?? cart?.subtotal ?? 0;
        if (subtotal < (codSettings.minOrderValueRupees || 0)) return false;
        if (codSettings.maxOrderValueRupees && subtotal > codSettings.maxOrderValueRupees) return false;
        if (address.pincode?.trim() && codSettings.pincodeAllowed === false) return false;
        return true;
    })();

    const coinCapRupees = (() => {
        if (!coinsOn || !loyalty) return 0;
        const pct = 20;
        const subtotal = cart?.subtotal || 0;
        return Math.min(loyalty.styleCoins || 0, Math.floor(subtotal * pct / 100));
    })();
    const effectiveCoins = Math.max(0, Math.min(coinsApplied, coinCapRupees));
    const baseTotal = cart?.grandTotal || 0;
    const afterCoins = Math.max(0, baseTotal - effectiveCoins);
    const codAdvance = codSettings?.upfrontPercent ? Math.ceil(afterCoins * codSettings.upfrontPercent / 100) : 0;
    const codBalance = Math.max(0, afterCoins - codAdvance);

    const handlePay = async (e) => {
        e.preventDefault();
        if (!cart || !cart.items?.length) { toast.error("Your cart is empty"); return; }
        for (const k of ["name", "phone", "email", "line1", "city", "state", "pincode"]) {
            if (!address[k] || !String(address[k]).trim()) { toast.error("Please fill all address fields"); return; }
        }
        setBusy(true);
        const ok = await loadRazorpayScript();
        if (!ok) { toast.error("Could not load payment gateway. Please retry."); setBusy(false); return; }
        try {
            const { data } = await customerApi.post("/checkout/create-order", {
                shippingAddress: address,
                paymentMethod,
                coinsApplied: effectiveCoins,
            });
            const opts = {
                key: data.keyId,
                order_id: data.razorpayOrderId,
                amount: data.amount,
                currency: data.currency,
                name: "Sukaanyas",
                description: paymentMethod === "cod" ? "Boutique order — COD advance" : "Boutique order",
                prefill: {
                    name: data.userName || address.name,
                    email: data.userEmail || address.email,
                    contact: address.phone,
                },
                theme: { color: "#61A5C2" },
                handler: async (resp) => {
                    try {
                        await customerApi.post("/checkout/verify", {
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                            order_id: data.orderId,
                        });
                        toast.success(paymentMethod === "cod"
                            ? `Advance paid. ₹${codBalance.toFixed(0)} will be collected on delivery.`
                            : "Payment successful! Your order is confirmed.");
                        navigate(`/orders/${data.orderId}?paid=1`, { replace: true });
                    } catch (e2) {
                        toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Payment verification failed");
                    }
                },
                modal: {
                    ondismiss: () => {
                        toast.message("Payment cancelled — your cart is intact.");
                        setBusy(false);
                    },
                },
            };
            const rzp = new window.Razorpay(opts);
            rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(false); });
            rzp.open();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not create order");
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-ivory">
                <Header />
                <div className="max-w-[1200px] mx-auto px-6 py-16 text-inkMuted">Loading…</div>
                <Footer />
            </div>
        );
    }

    if (!cart || cart.items.length === 0) {
        return (
            <div className="min-h-screen bg-ivory" data-testid="checkout-empty">
                <Header />
                <div className="max-w-[1200px] mx-auto px-6 py-16">
                    <h1 className="font-display text-3xl">Your bag is empty</h1>
                    <p className="mt-2 text-inkMuted">Add a piece before checkout.</p>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ivory" data-testid="checkout-page">
            <Header />
            <main className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12">
                <span className="label-eyebrow">Final step</span>
                <h1 className="mt-3 font-display text-4xl sm:text-5xl text-inkDark tracking-tight">Checkout</h1>

                <form onSubmit={handlePay} className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="checkout-form">
                    <div className="lg:col-span-7 bg-ivory border border-inkDark/5 rounded-2xl p-6 lg:p-8 space-y-4">
                        <div className="font-display text-2xl text-inkDark">Shipping address</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Full name">
                                <input required value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="checkout-name-input" />
                            </Field>
                            <Field label="Phone">
                                <input required value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="checkout-phone-input" />
                            </Field>
                        </div>
                        <Field label="Email">
                            <input type="email" required value={address.email} onChange={(e) => setAddress({ ...address, email: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="checkout-email-input" />
                        </Field>
                        <Field label="Address line 1">
                            <input required value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="checkout-line1-input" />
                        </Field>
                        <Field label="Address line 2 (optional)">
                            <input value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                        </Field>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <Field label="City">
                                <input required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="checkout-city-input" />
                            </Field>
                            <Field label="State">
                                <input required value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="checkout-state-input" />
                            </Field>
                            <Field label="Pincode">
                                <input required value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="checkout-pincode-input" />
                            </Field>
                        </div>
                        <Field label="Country">
                            <input value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                        </Field>
                    </div>

                    <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start space-y-5">
                        <div className="bg-beige rounded-2xl p-6" data-testid="checkout-summary">
                            <div className="font-display text-2xl text-inkDark">Order summary</div>
                            <ul className="mt-4 divide-y divide-inkDark/10">
                                {cart.items.map((it) => (
                                    <li key={it.id} className="py-3 flex gap-3 text-sm">
                                        <img src={it.image} alt="" className="w-12 h-16 rounded-md object-cover" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-inkDark line-clamp-1">{it.name}</div>
                                            <div className="text-inkMuted text-xs">Qty {it.qty}{it.size ? ` · Size ${it.size}` : ""}</div>
                                        </div>
                                        <div className="text-inkDark">{formatINR(it.lineTotal)}</div>
                                    </li>
                                ))}
                            </ul>
                            <dl className="mt-4 space-y-2 text-sm border-t border-inkDark/10 pt-4">
                                <div className="flex justify-between"><dt className="text-inkMuted">Subtotal</dt><dd>{formatINR(cart.subtotal)}</dd></div>
                                {cart.discount > 0 ? (
                                    <div className="flex justify-between text-skyblueDeep"><dt>Discount {cart.couponCode ? `(${cart.couponCode})` : ""}</dt><dd>−{formatINR(cart.discount)}</dd></div>
                                ) : null}
                                {coinsOn && effectiveCoins > 0 ? (
                                    <div className="flex justify-between text-skyblueDeep"><dt>Style Coins</dt><dd>−{formatINR(effectiveCoins)}</dd></div>
                                ) : null}
                                <div className="flex justify-between"><dt className="text-inkMuted">Shipping</dt><dd>Free</dd></div>
                                <div className="border-t border-inkDark/10 pt-2 flex justify-between font-medium text-base"><dt>Total</dt><dd data-testid="checkout-total">{formatINR(afterCoins)}</dd></div>
                                {paymentMethod === "cod" ? (
                                    <>
                                        <div className="flex justify-between text-xs pt-1"><dt>Pay now (advance {codSettings?.upfrontPercent || 25}%)</dt><dd data-testid="checkout-cod-advance">{formatINR(codAdvance)}</dd></div>
                                        <div className="flex justify-between text-xs"><dt>Collected on delivery</dt><dd data-testid="checkout-cod-balance">{formatINR(codBalance)}</dd></div>
                                    </>
                                ) : null}
                            </dl>

                            {coinsOn && loyalty && loyalty.styleCoins > 0 ? (
                                <div className="mt-4 rounded-xl border border-inkDark/5 bg-ivory p-3" data-testid="coin-redeem-widget">
                                    <div className="flex items-center justify-between text-xs mb-2">
                                        <span className="label-eyebrow">Style Coins</span>
                                        <span className="text-inkMuted">Balance: {loyalty.styleCoins} · Max: {coinCapRupees}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min={0}
                                            max={coinCapRupees}
                                            value={coinsApplied}
                                            onChange={(e) => setCoinsApplied(Math.max(0, Math.min(coinCapRupees, parseInt(e.target.value || "0", 10))))}
                                            className="w-24 rounded-lg border border-inkDark/10 px-3 py-1.5 text-sm"
                                            data-testid="coin-apply-input"
                                        />
                                        <button type="button" onClick={() => setCoinsApplied(coinCapRupees)} className="text-xs text-skyblueDeep underline" data-testid="coin-apply-max">Apply max</button>
                                        {coinsApplied > 0 ? (
                                            <button type="button" onClick={() => setCoinsApplied(0)} className="text-xs text-inkMuted underline ml-auto" data-testid="coin-apply-clear">Remove</button>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}

                            {codOn && codSettings?.enabled ? (
                                <div className="mt-4 space-y-2" data-testid="payment-method-picker">
                                    <span className="label-eyebrow">Payment method</span>
                                    <label className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer ${paymentMethod === "prepaid" ? "border-skyblueDeep bg-skyblue/10" : "border-inkDark/10"}`}>
                                        <input type="radio" name="pm" value="prepaid" checked={paymentMethod === "prepaid"} onChange={() => setPaymentMethod("prepaid")} data-testid="payment-prepaid-radio" />
                                        <span>
                                            <span className="block text-sm font-medium">Prepaid — Pay {formatINR(afterCoins)} now</span>
                                            <span className="block text-xs text-inkMuted">UPI · Cards · Netbanking via Razorpay</span>
                                        </span>
                                    </label>
                                    <label className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer ${paymentMethod === "cod" ? "border-skyblueDeep bg-skyblue/10" : "border-inkDark/10"} ${!codEligible ? "opacity-60" : ""}`}>
                                        <input type="radio" name="pm" value="cod" disabled={!codEligible} checked={paymentMethod === "cod"} onChange={() => setPaymentMethod("cod")} data-testid="payment-cod-radio" />
                                        <span className="flex-1">
                                            <span className="block text-sm font-medium">Cash on Delivery — Pay {formatINR(codAdvance)} now, {formatINR(codBalance)} on delivery</span>
                                            <span className="block text-xs text-inkMuted">
                                                {codEligible
                                                    ? "Advance is non-refundable on customer cancellation. Exchange only on defective items."
                                                    : (codSettings.pincodeAllowed === false
                                                        ? "Your pincode is not COD-serviceable yet."
                                                        : `COD available between ₹${codSettings.minOrderValueRupees} and ₹${codSettings.maxOrderValueRupees}.`)}
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            ) : null}

                            <button type="submit" disabled={busy} className="btn-pill w-full justify-center mt-6 disabled:opacity-50" data-testid="checkout-pay-button">
                                {busy
                                    ? "Opening Razorpay…"
                                    : paymentMethod === "cod"
                                        ? `Pay advance ${formatINR(codAdvance)}`
                                        : `Pay ${formatINR(afterCoins)}`}
                            </button>
                            <div className="mt-3 text-xs text-inkMuted text-center">Secured by Razorpay · UPI · Cards · Netbanking</div>
                        </div>
                    </aside>
                </form>
            </main>
            <Footer />
            <ChatbotWidget />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-eyebrow">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
